// Fiche du studio : historique immuable, comparaison et gestes explicites sur une version.
import { useState, type JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Select } from '@artosera/ui';
import type { TexteStudio } from '@artosera/shared';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { estPorteeObsolete } from '../../auth/identite.js';
import { enregistrerBlob } from '../../api/client.js';
import { useTexteStudio, useGesteStudio } from '../../api/studio.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { VersionStudio, useDisponibiliteStudio, erreurStudio } from './commun.js';
import styles from './Studio.module.css';

export function TexteStudioPage(): JSX.Element {
  const { galerie, compte, galerieSlug } = useShellContext(),
    { id = '' } = useParams();
  if (!galerieAModule(galerie, 'studio-contenu') || !galerieALaction(galerie, 'studio:read'))
    return <NotFoundPage />;
  return (
    <Fiche
      key={`${compte.id}:${galerieSlug}:${id}:${galerie.actions.join(',')}:${galerie.modules?.join(',')}`}
      id={id}
    />
  );
}

function Fiche({ id }: { id: string }): JSX.Element {
  const { galerieSlug } = useShellContext(),
    { t } = useTranslation('regie');
  const fiche = useTexteStudio(galerieSlug, id);
  return (
    <div className={styles.page}>
      <Link to={`/${galerieSlug}/studio`}>{t('studio.retour')}</Link>
      {fiche.isPending && <p>{t('studio.chargement')}</p>}
      {fiche.isError && <p role="alert">{t('studio.erreur')}</p>}
      {fiche.data && (
        <Texte
          texte={fiche.data.texte}
          recharger={async () => {
            await fiche.refetch();
          }}
        />
      )}
    </div>
  );
}

function Texte({
  texte,
  recharger,
}: {
  texte: TexteStudio;
  recharger: () => Promise<void>;
}): JSX.Element {
  const { t, i18n } = useTranslation('regie'),
    { galerieSlug, galerie } = useShellContext(),
    navigate = useNavigate();
  const [n, setN] = useState(texte.versions.at(-1)!.n),
    [comparaison, setComparaison] = useState<number | null>(null);
  const courante = texte.versions.find((v) => v.n === n) ?? texte.versions.at(-1)!;
  const [archivageOuvert, setArchivageOuvert] = useState(false);
  const [edition, setEdition] = useState(false),
    [contenu, setContenu] = useState(courante.contenu),
    [verifications, setVerifications] = useState(courante.aVerifier.join('\n'));
  const [consigne, setConsigne] = useState(''),
    [notice, setNotice] = useState<string | null>(null),
    [erreur, setErreur] = useState<string | null>(null);
  const geste = useGesteStudio(galerieSlug),
    { raison, disponible } = useDisponibiliteStudio(),
    { raison: raisonTraduction, disponible: traductionDisponible } =
      useDisponibiliteStudio('studio:traduction');
  const ecriture = galerieALaction(galerie, 'studio:write'),
    modifiable = ecriture && texte.etat !== 'archive',
    utilisable = ecriture && texte.etat === 'valide';
  async function agir(action: string, corps: object = {}) {
    setErreur(null);
    setNotice(null);
    try {
      const r = await geste.mutateAsync({
        id: texte.id,
        geste: action,
        corps: { version: texte.version, ...corps },
      });
      if (action === 'traduire') {
        void navigate(`/${galerieSlug}/studio/${r.texte.id}`);
      } else {
        setNotice(t('studio.enregistre'));
        setEdition(false);
        if (action === 'modifier' || action === 'retoucher') setN(r.texte.versions.at(-1)!.n);
      }
      return r;
    } catch (e) {
      if (!estPorteeObsolete(e)) {
        setErreur(erreurStudio(e, t));
        await recharger();
      }
      return null;
    }
  }
  return (
    <>
      <header className={styles.entete}>
        <div>
          <h1>{texte.titreInterne}</h1>
          <p>
            {t(`studio.types.${texte.type}`)} · {t(`studio.etats.${texte.etat}`)} ·{' '}
            {t(`studio.langues.${texte.langue}`)}
          </p>
        </div>
        {modifiable && (
          <Dialog
            open={archivageOuvert}
            onOpenChange={(ouvert) => {
              if (!geste.isPending) setArchivageOuvert(ouvert);
            }}
            title={t('studio.confirmationArchivageTitre')}
            description={t('studio.confirmationArchivageTexte')}
            closeLabel={t('commun:actions.fermer')}
            trigger={
              <Button variant="danger" disabled={geste.isPending}>
                {t('studio.archiver')}
              </Button>
            }
            footer={
              <>
                <Button
                  variant="secondary"
                  disabled={geste.isPending}
                  onClick={() => setArchivageOuvert(false)}
                >
                  {t('studio.annuler')}
                </Button>
                <Button
                  variant="danger"
                  loading={geste.isPending}
                  onClick={async () => {
                    await agir('archiver');
                    setArchivageOuvert(false);
                  }}
                >
                  {t('studio.archiver')}
                </Button>
              </>
            }
          />
        )}
      </header>
      <p>{texte.brief}</p>
      {Object.entries(texte.traductions).map(([langue, id]) => (
        <p key={langue}>
          <Link to={`/${galerieSlug}/studio/${id}`}>
            {t('studio.texteLie', { langue: langue.toUpperCase() })}
          </Link>
        </p>
      ))}
      {(raison ?? raisonTraduction) && ecriture && (
        <p role="status" className={styles.notice}>
          {raison ?? raisonTraduction} {t('studio.sansIa')}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {erreur && <p role="alert">{erreur}</p>}
      <div className={styles.filtres}>
        <Field htmlFor="studio-historique" label={t('studio.historique')}>
          <Select
            id="studio-historique"
            value={String(courante.n)}
            onValueChange={(valeur) => {
              setN(Number(valeur));
              setEdition(false);
            }}
            options={[...texte.versions].reverse().map((v) => ({
              value: String(v.n),
              label: `${t('studio.version', { n: v.n })} · ${t(`studio.origines.${v.origine}`)}${
                texte.versionValidee === v.n ? ` · ${t('studio.etats.valide')}` : ''
              }`,
            }))}
          />
        </Field>
        <Field htmlFor="studio-comparaison" label={t('studio.comparer')}>
          <Select
            id="studio-comparaison"
            value={comparaison === null ? 'aucune' : String(comparaison)}
            onValueChange={(valeur) => setComparaison(valeur === 'aucune' ? null : Number(valeur))}
            options={[
              { value: 'aucune', label: t('studio.aucune') },
              ...texte.versions.map((v) => ({
                value: String(v.n),
                label: t('studio.version', { n: v.n }),
              })),
            ]}
          />
        </Field>
      </div>
      <p>
        {t('studio.version', { n })} ·{' '}
        {new Intl.DateTimeFormat(i18n.language, {
          dateStyle: 'medium',
          timeStyle: 'short',
          timeZone: 'Europe/Paris',
        }).format(new Date(courante.creeLe))}
        {courante.n === texte.versionValidee && <span> · {t('studio.versionValidee')}</span>}
      </p>
      <div className={comparaison === null ? undefined : styles.comparaison}>
        <VersionStudio version={courante} />
        {comparaison !== null && (
          <VersionStudio
            version={texte.versions.find((v) => v.n === comparaison) ?? texte.versions[0]!}
          />
        )}
      </div>
      {modifiable && (
        <>
          <div className={styles.actions}>
            <Button
              variant="secondary"
              disabled={geste.isPending}
              onClick={() => {
                setContenu(courante.contenu);
                setVerifications(courante.aVerifier.join('\n'));
                setEdition(true);
              }}
            >
              {t('studio.modifier')}
            </Button>
            {courante.n !== texte.versionValidee && (
              <Button
                disabled={geste.isPending}
                onClick={() => void agir('valider', { n: courante.n })}
              >
                {t('studio.valider')}
              </Button>
            )}
            <Button
              variant="secondary"
              disabled={geste.isPending || !traductionDisponible}
              onClick={() => void agir('traduire', { n })}
            >
              {t('studio.traduire')}
            </Button>
          </div>
          {edition && (
            <form
              className={styles.formulaire}
              onSubmit={(e) => {
                e.preventDefault();
                void agir('modifier', {
                  contenu,
                  aVerifier: verifications
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                });
              }}
            >
              {(['titre', 'chapo', 'corps'] as const).map((cle) => (
                <label key={cle}>
                  {t(`studio.contenu.${cle}`)}
                  <textarea
                    value={contenu[cle]}
                    required={cle === 'corps'}
                    maxLength={cle === 'corps' ? 16000 : cle === 'titre' ? 500 : 2000}
                    onChange={(e) => setContenu({ ...contenu, [cle]: e.target.value })}
                  />
                </label>
              ))}
              <label>
                {t('studio.aVerifier')}
                <textarea
                  value={verifications}
                  onChange={(e) => setVerifications(e.target.value)}
                />
              </label>
              <Button type="submit" disabled={geste.isPending}>
                {t('studio.enregistrerVersion')}
              </Button>
              <Button variant="secondary" onClick={() => setEdition(false)}>
                {t('studio.annuler')}
              </Button>
            </form>
          )}
          <form
            className={styles.formulaire}
            onSubmit={(e) => {
              e.preventDefault();
              void agir('retoucher', { n, consigne });
            }}
          >
            <label>
              {t('studio.consigne')}
              <input
                required
                maxLength={300}
                value={consigne}
                onChange={(e) => setConsigne(e.target.value)}
              />
            </label>
            <Button
              variant="secondary"
              type="submit"
              disabled={geste.isPending || !disponible || !consigne.trim()}
            >
              {t('studio.retoucher')}
            </Button>
          </form>
        </>
      )}
      <section>
        <h2>{t('studio.utiliser')}</h2>
        {!utilisable && <p>{t('studio.validationRequise')}</p>}
        {utilisable && (
          <div className={styles.actions}>
            <Button
              disabled={geste.isPending}
              onClick={async () => {
                const r = await agir('utiliser', { type: 'copie' });
                if (r?.contenu) {
                  try {
                    await navigator.clipboard.writeText(r.contenu);
                    setNotice(t('studio.copieFaite'));
                  } catch {
                    setErreur(t('studio.copieImpossible'));
                  }
                }
              }}
            >
              {t('studio.copier')}
            </Button>
            <Button
              variant="secondary"
              disabled={geste.isPending}
              onClick={async () => {
                const r = await agir('utiliser', { type: 'telechargement' });
                if (r?.contenu)
                  enregistrerBlob(
                    new Blob([r.contenu], { type: 'text/plain;charset=utf-8' }),
                    `studio-${texte.id}-${texte.langue}.txt`,
                  );
              }}
            >
              {t('studio.telecharger')}
            </Button>
            {galerieAModule(galerie, 'site') && galerieALaction(galerie, 'posts:write') && (
              <Button
                variant="secondary"
                disabled={geste.isPending}
                onClick={async () => {
                  const r = await agir('utiliser', { type: 'actualite' });
                  if (r?.postId) setNotice(t('studio.actualiteCreee'));
                }}
              >
                {t('studio.creerActualite')}
              </Button>
            )}
          </div>
        )}
        <ul>
          {texte.utilisations.map((u, i) => (
            <li key={i}>
              {t(`studio.utilisations.${u.type}`)} ·{' '}
              {new Intl.DateTimeFormat(i18n.language, {
                dateStyle: 'medium',
                timeZone: 'Europe/Paris',
              }).format(new Date(u.le))}
              {u.postId && ` · ${u.postId}`}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
